import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle2, XCircle, AlertTriangle, Clock, Play, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';


interface ExecutionSummary {
  id: string;
  execution_id: string;
  total_tests: number;
  passed: number;
  failed: number;
  errors: number;
  total_duration_ms: number;
  environment: string;
  triggered_by: string;
  suites_summary: Array<{ name: string; passed: number; failed: number; errors: number; total: number }>;
  created_at: string;
}

interface TestExecution {
  id: string;
  execution_id: string;
  suite_name: string;
  test_name: string;
  status: string;
  duration_ms: number;
  error_message: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

const QADashboardPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { toast } = useToast();
  const [running, setRunning] = React.useState(false);
  const [selectedExecution, setSelectedExecution] = React.useState<string | null>(null);

  const { data: summaries = [], isLoading, refetch } = useQuery({
    queryKey: ['qa-summaries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('qa_execution_summary')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data || []) as unknown as ExecutionSummary[];
    },
  });

  const { data: details = [] } = useQuery({
    queryKey: ['qa-details', selectedExecution],
    queryFn: async () => {
      if (!selectedExecution) return [];
      const { data, error } = await supabase
        .from('qa_test_executions')
        .select('*')
        .eq('execution_id', selectedExecution)
        .order('suite_name', { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as TestExecution[];
    },
    enabled: !!selectedExecution,
  });

  const handleRunTests = async () => {
    setRunning(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error('Não autenticado');

      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/run-qa-financial-tests`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ triggered_by: 'manual' }),
        },
      );
      const result = await res.json();
      if (result.success) {
        toast({
          title: `Testes executados: ${result.passed}/${result.total} passaram`,
          description: `Duração: ${result.duration_ms}ms`,
          variant: result.failed > 0 ? 'destructive' : 'default',
        });
        refetch();
      } else {
        throw new Error(result.error || 'Erro desconhecido');
      }
    } catch (err: any) {
      toast({ title: 'Erro ao executar testes', description: err.message, variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  const latest = summaries[0];

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header — hidden when embedded as tab in Ferramentas Dev */}
        {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">QA Dashboard</h1>
            <p className="text-sm text-muted-foreground">Validação automatizada de integridade dos fluxos financeiros</p>
          </div>
          <Button onClick={handleRunTests} disabled={running} className="gap-2">
            {running ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            {running ? 'Executando...' : 'Executar Agora'}
          </Button>
        </div>
        )}

        {/* Latest Summary KPIs */}
        {latest && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card>
              <CardContent className="pt-4 text-center">
                <p className="text-xs text-muted-foreground">Total Testes</p>
                <p className="text-3xl font-bold">{latest.total_tests}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-4 text-center">
                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-green-500" /> Passou
                </p>
                <p className="text-3xl font-bold text-green-600">{latest.passed}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-4 text-center">
                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <XCircle className="h-3 w-3 text-red-500" /> Falhou
                </p>
                <p className="text-3xl font-bold text-red-600">{latest.failed}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="pt-4 text-center">
                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <AlertTriangle className="h-3 w-3 text-yellow-500" /> Erros
                </p>
                <p className="text-3xl font-bold text-yellow-600">{latest.errors}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 text-center">
                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <Clock className="h-3 w-3" /> Duração
                </p>
                <p className="text-3xl font-bold">{(latest.total_duration_ms / 1000).toFixed(1)}s</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Suites Breakdown (latest) */}
        {latest && latest.suites_summary && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Suítes — Última Execução</CardTitle>
              <CardDescription className="text-xs">
                {format(new Date(latest.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })} 
                — via {latest.triggered_by}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {(latest.suites_summary as any[]).map((suite: any) => (
                  <div key={suite.name} className="border rounded-lg p-3 text-center">
                    <p className="text-xs font-medium text-muted-foreground mb-1 truncate">{suite.name}</p>
                    <Badge variant={suite.failed > 0 || suite.errors > 0 ? 'destructive' : 'default'} className="text-xs">
                      {suite.passed}/{suite.total}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Execution History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Histórico de Execuções</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Passou</TableHead>
                  <TableHead>Falhou</TableHead>
                  <TableHead>Duração</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summaries.map((s) => (
                  <TableRow key={s.id} className={selectedExecution === s.execution_id ? 'bg-muted/50' : ''}>
                    <TableCell className="text-sm">
                      {format(new Date(s.created_at), "dd/MM HH:mm", { locale: ptBR })}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{s.triggered_by}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{s.total_tests}</TableCell>
                    <TableCell className="text-green-600 font-medium">{s.passed}</TableCell>
                    <TableCell className="text-red-600 font-medium">{s.failed + s.errors}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{(s.total_duration_ms / 1000).toFixed(1)}s</TableCell>
                    <TableCell>
                      {s.failed === 0 && s.errors === 0 ? (
                        <Badge variant="default" className="gap-1"><CheckCircle2 className="h-3 w-3" /> OK</Badge>
                      ) : (
                        <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" /> FALHA</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setSelectedExecution(s.execution_id === selectedExecution ? null : s.execution_id)}
                      >
                        Detalhes
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {summaries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Nenhuma execução registrada. Clique em "Executar Agora" para iniciar.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Test Details Drilldown */}
        {selectedExecution && details.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Detalhes da Execução</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Suíte</TableHead>
                    <TableHead>Teste</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Duração</TableHead>
                    <TableHead>Erro</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {details.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="text-xs font-medium">{d.suite_name}</TableCell>
                      <TableCell className="text-sm">{d.test_name}</TableCell>
                      <TableCell>
                        {d.status === 'passed' ? (
                          <Badge variant="default" className="gap-1 text-xs"><CheckCircle2 className="h-3 w-3" /> OK</Badge>
                        ) : (
                          <Badge variant="destructive" className="gap-1 text-xs"><XCircle className="h-3 w-3" /> {d.status}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{d.duration_ms}ms</TableCell>
                      <TableCell className="text-xs text-destructive max-w-xs truncate">{d.error_message || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
    </div>
  );
};

export default QADashboardPage;
